import { useCallback, useEffect, useState } from "react"
import { useCallSheetData } from "./useCallSheetData"
import { NON_BILLABLE_OPTIONS } from "../constants"
import { useCallFormState } from "./useCallFormState"

export const useCallFormFields = (answeredCall, setServerState) => {
  const [callerAccessCodeId, setCallerAccessCodeId] = useState(null)
  const [initialPartner, setInitialPartner] = useState(null)
  const [meetingUrl, setMeetingUrl] = useState(null)
  const [showCollectLocation, setShowCollectLocation] = useState(false)
  const [showCollectDepartment, setShowCollectDepartment] = useState(false)
  const { callSheetData, getCallSheetData } = useCallSheetData(answeredCall?.interaction?.id)

  const { reset, onCallSheetLoad, ...rest } = useCallFormState()

  const { setInteractionId, setInteractionName, setUnitId, setCustomerPhoneNumber, setLanguage } = rest

  const refreshCallSheet = useCallback(() => {
    return getCallSheetData()
  }, [getCallSheetData])

  useEffect(() => {
    if (!(answeredCall?.interaction || answeredCall?.language)) {
      return
    }
    /* NOTE:
      This assumes that neither the interaction nor language objects ever change once set.
      Since some of this data can come from the interaction object instead, they will be nullified if answeredCall updates.
    */
    const { id, name, unitId, customerPhoneNumber } = answeredCall.interaction

    setInteractionId(id)
    setInteractionName(name)
    setUnitId(unitId || "")
    setCustomerPhoneNumber(customerPhoneNumber)
    setLanguage(answeredCall.language?.name)
    return () => {
      reset()
    }
  }, [
    answeredCall?.interaction,
    answeredCall?.language,
    reset,
    setCustomerPhoneNumber,
    setInteractionId,
    setInteractionName,
    setLanguage,
    setUnitId,
  ])

  useEffect(() => {
    if (callSheetData) {
      const {
        partnerId,
        departmentId,
        providerFirstName,
        providerLastName,
        patientFirstName,
        patientLastName,
        patientMRN,
        patientDOB,
        nonBillableReason,
        phoneNumberAC,
        bchOaklandHospitalConfirmation,
        location,
        memberIdNumber,
        nyuAccessCode,
        sentaraAccessCode,
        visitId,
        callerFirstName,
        callerLastName,
        meetingUrl,
        showCollectLocation: collectLocation,
        showCollectDepartment: collectDepartment,
      } = callSheetData
      setCallerAccessCodeId(callSheetData.accessCodeId)
      setShowCollectLocation(collectLocation ?? false)
      setShowCollectDepartment(collectDepartment ?? false)

      onCallSheetLoad(callSheetData)
      setMeetingUrl(meetingUrl)
      setServerState({
        partnerId: partnerId || "",
        nonBillableReason: nonBillableReason === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReason,
        departmentId: departmentId || "",
        patientFirstName: patientFirstName || "",
        patientLastName: patientLastName || "",
        patientMRN: patientMRN,
        patientDOB: patientDOB || null,
        providerFirstName: providerFirstName || "",
        providerLastName: providerLastName || "",
        phoneNumberAC: phoneNumberAC || "",
        bchOaklandHospitalConfirmation: bchOaklandHospitalConfirmation || false,
        location: location || "",
        memberIdNumber: memberIdNumber || "",
        nyuAccessCode: nyuAccessCode || "",
        sentaraAccessCode: sentaraAccessCode || "",
        visitId: visitId || "",
        callerFirstName: callerFirstName || "",
        callerLastName: callerLastName || "",
      })
    }
  }, [callSheetData, onCallSheetLoad, setServerState])

  useEffect(() => {
    // This is a hack to remember the initial partner name (ie. the first partner name on a call)
    // TODO: remove this useEffect and "initialPartnerName" when this is stored elsewhere (like the backend interaction object)... However keep all the state resetting logic
    if (!answeredCall) {
      setCallerAccessCodeId(null)
      setInitialPartner(null)
      setMeetingUrl(null)
      setShowCollectLocation(false)
      setShowCollectDepartment(false)
      setInteractionId("")
      setInteractionName("")
      setUnitId("")
      setCustomerPhoneNumber("")
      setLanguage("")
      return
    }
    if (initialPartner?.id || !rest.partnerId) {
      return
    }
    setInitialPartner({ name: rest.partnerName, id: rest.partnerId })
  }, [
    rest,
    answeredCall,
    initialPartner?.id,
    setInteractionId,
    setInteractionName,
    setUnitId,
    setCustomerPhoneNumber,
    setLanguage,
  ])

  return {
    ...rest,
    partnerNameInitial: initialPartner?.name,
    partnerIdInitial: initialPartner?.id,
    refreshCallSheet,
    callerAccessCodeId,
    showCollectLocation,
    showCollectDepartment,
    meetingUrl,
  }
}
